'use client';

import { useState, useEffect } from 'react';
import {
  Sparkles,
  Layers,
  LayoutGrid,
  Undo2,
  Copy
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { AskAIBar } from '@/components/ai';
import { VersionsDropdown, type Version } from '@/components/versions';
import { ArchitectureTemplate, ApplicationAreasTemplate } from './templates';
import { ArtifactGeneratorInput, type AttachedFile } from '@/components/shared/artifact-generator-input';
import { useToast } from '@/hooks/use-toast';
import { useAuthToken } from '@/hooks/use-auth';
import { useQueryClient } from '@tanstack/react-query';
import { useDesignVersions, useDesignArtifact, extractDesignMarkdown, type DesignType } from '@/hooks/use-design';

type DesignView = 'architecture' | 'areas';

interface DesignConfig {
  id: DesignView;
  title: string;
  subtitle: string;
  icon: React.ElementType;
  emptyTitle: string;
  emptyDescription: string;
  placeholder: string;
  askAIPlaceholder: string;
}

const designConfigs: Record<DesignView, DesignConfig> = {
  architecture: {
    id: 'architecture',
    title: 'Product Architecture',
    subtitle: 'Product architecture for',
    icon: Layers,
    emptyTitle: 'Define Product Architecture',
    emptyDescription: 'Generate comprehensive product architecture by describing system components, data flows, and integration points, or upload existing architecture documents.',
    placeholder: "Describe your product architecture... (e.g., 'Frontend React app, Node.js API, PostgreSQL database, Redis cache')",
    askAIPlaceholder: "E.g., Add caching strategy, focus on scalability, include monitoring...",
  },
  areas: {
    id: 'areas',
    title: 'Application Areas',
    subtitle: 'Application areas for',
    icon: LayoutGrid,
    emptyTitle: 'Define Application Areas',
    emptyDescription: 'Generate application area definitions by describing functional domains, feature groups, and module boundaries, or upload product specifications.',
    placeholder: "Describe your application areas... (e.g., 'User management, Dashboard analytics, Reporting module, Settings')",
    askAIPlaceholder: "E.g., Add admin area, focus on mobile-specific areas, include integrations...",
  },
};

interface DesignPanelProps {
  projectId?: string;
  projectName?: string;
  activeView?: DesignView;
  areaName?: string; // For specific area like "Area 1"
  onViewChange?: (view: DesignView) => void;
  onAskAI?: (message: string) => void;
  onVersionSelect?: (version: Version) => void;
  onUndo?: () => void;
  onCopy?: () => void;
  onContentGenerated?: (content: string) => void;
}

export function DesignPanel({
  projectId,
  projectName = 'Enterprise Banking Platform',
  activeView = 'architecture',
  areaName,
  onViewChange,
  onAskAI,
  onVersionSelect,
  onUndo,
  onCopy,
  onContentGenerated,
}: DesignPanelProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [showAskAI, setShowAskAI] = useState(false);
  const [contentByView, setContentByView] = useState<Record<string, string>>({});
  const [showGenerateFormByView, setShowGenerateFormByView] = useState<Record<string, boolean>>({});
  const [isResetting, setIsResetting] = useState(false);
  const { toast } = useToast();
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const designType = activeView === 'architecture' ? 'architecture' : 'areas';

  // Fetch persisted artifact for current view (enables content after reload)
  const { data: artifact } = useDesignArtifact(projectId, designType);

  // Hydrate contentByView from persisted artifact when it loads
  useEffect(() => {
    const markdown = artifact ? extractDesignMarkdown(artifact.content) : null;
    if (markdown && projectId) {
      setContentByView(prev => ({ ...prev, [activeView]: markdown }));
    }
  }, [artifact, activeView, projectId]);

  // Prefer local state, fall back to artifact (ensures document shows on reload before useEffect runs)
  const artifactMarkdown = artifact ? extractDesignMarkdown(artifact.content) : null;
  const generatedContent = contentByView[activeView] ?? artifactMarkdown ?? null;
  const showForm = showGenerateFormByView[activeView] ?? false;
  const setGeneratedContent = (content: string | null) => {
    setContentByView(prev => {
      if (content === null) {
        const next = { ...prev };
        delete next[activeView];
        return next;
      }
      return { ...prev, [activeView]: content };
    });
  };

  // Fetch versions from API
  const { data: versions = [] } = useDesignVersions(
    projectId,
    activeView as DesignType
  );

  const config = designConfigs[activeView];
  const Icon = config.icon;

  // For specific areas, customize the title and subtitle
  const displayTitle = areaName || config.title;
  const displaySubtitle = areaName 
    ? `Application area for ${projectName}` 
    : `${config.subtitle} ${projectName}`;

  const handleGenerate = async (description: string, attachedFiles: AttachedFile[]) => {
    if (!projectId) {
      toast({
        title: 'Error',
        description: 'Project ID is required',
        variant: 'destructive',
      });
      return;
    }

    if (!token) {
      toast({
        title: 'Authentication Required',
        description: 'Please log in to generate content.',
        variant: 'destructive',
      });
      return;
    }

    setIsGenerating(true);
    try {
      const fileContext = attachedFiles.map(f => ({
        name: f.name,
        type: f.type,
        size: f.size,
      }));

      const response = await fetch(`/api/projects/${projectId}/design/${designType}/generate`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          prompt: description,
          attachedFiles: fileContext,
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        // Backend returns { artifact: { content: { markdown: ... } } }
        const content = data.data.artifact?.content || data.data.content;
        // Extract markdown from content object, or use content directly if it's a string
        let markdownContent: string;
        if (typeof content === 'object' && content !== null) {
          // New format: { markdown: "...", generatedAt: "...", model: "..." }
          markdownContent = content.markdown || content.content || JSON.stringify(content, null, 2);
        } else {
          markdownContent = String(content || '');
        }
        setGeneratedContent(markdownContent);
        setShowGenerateFormByView(prev => ({ ...prev, [activeView]: false }));
        onContentGenerated?.(markdownContent);
        queryClient.invalidateQueries({ queryKey: ['design', projectId, designType, 'artifact'] });
        queryClient.invalidateQueries({ queryKey: ['design', projectId, 'list'] });
        toast({
          title: 'Content Generated',
          description: 'AI has generated your content successfully.',
        });
      } else {
        throw new Error(data.message || 'Failed to generate content');
      }
    } catch (error) {
      toast({
        title: 'Generation Failed',
        description: error instanceof Error ? error.message : 'Failed to generate content',
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleReset = async () => {
    if (!projectId || !token) return;
    setIsResetting(true);
    try {
      const response = await fetch(`/api/projects/${projectId}/design/${designType}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error?.message || 'Failed to delete document');
      }
      setGeneratedContent(null);
      setShowGenerateFormByView(prev => ({ ...prev, [activeView]: true }));
      queryClient.invalidateQueries({ queryKey: ['design', projectId, designType, 'artifact'] });
      queryClient.invalidateQueries({ queryKey: ['design', projectId, designType, 'versions'] });
      queryClient.invalidateQueries({ queryKey: ['design', projectId, 'list'] });
      toast({ title: 'Document Reset', description: 'Document deleted. You can generate a new one.' });
    } catch (error) {
      toast({
        title: 'Reset Failed',
        description: error instanceof Error ? error.message : 'Failed to delete document',
        variant: 'destructive',
      });
    } finally {
      setIsResetting(false);
    }
  };

  const handleAskAI = async (message: string) => {
    console.log('Asking AI:', message);
    await onAskAI?.(message);
    toast({
      title: "AI Request Sent",
      description: "Your request is being processed...",
    });
  };

  const handleVersionSelect = (version: Version) => {
    console.log('Selected version:', version);
    onVersionSelect?.(version);
  };

  const handleUndo = async () => {
    if (generatedContent && !showForm) {
      await handleReset();
    } else {
      onUndo?.();
      toast({
        title: "Undo",
        description: "Last action undone",
      });
    }
  };

  const handleCopy = () => {
    console.log('Copy content');
    onCopy?.();
    toast({
      title: "Copied",
      description: "Content copied to clipboard",
    });
  };

  return (
    <div className="flex flex-col h-full w-full">
      {/* FIXED HEADER - Does NOT scroll */}
      <div className="shrink-0 bg-white px-8 py-6 border-b border-gray-200">
        <div className="flex items-start justify-between">
          {/* Title */}
          <div>
            <h1 className="text-2xl font-medium text-gray-900 tracking-tight">
              {displayTitle}
            </h1>
            <p className="text-base text-gray-600 mt-1">
              {displaySubtitle}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {/* Ask AI Button */}
            <button
              onClick={() => setShowAskAI(!showAskAI)}
              className={cn(
                "h-[42px] px-4 flex items-center gap-2 rounded-[10px] transition-colors text-white",
                showAskAI 
                  ? "bg-brand-primary/80" 
                  : "bg-brand-primary hover:bg-brand-primary/90"
              )}
            >
              <Sparkles className="w-4 h-4" />
              <span className="text-base">Ask AI</span>
            </button>

            {/* Versions Dropdown */}
            <VersionsDropdown
              versions={versions}
              onVersionSelect={handleVersionSelect}
            />

            {/* Undo Button - only show when document is displayed */}
            {generatedContent && !showForm && (
              <button 
                onClick={handleUndo}
                disabled={isResetting}
                className="h-[42px] px-4 flex items-center gap-2 border border-gray-300 rounded-[10px] hover:bg-gray-50 transition-colors text-gray-700 disabled:opacity-50"
              >
                <Undo2 className="w-4 h-4" />
                <span className="text-base">{isResetting ? 'Resetting...' : 'Undo'}</span>
              </button>
            )}

            {/* Copy Button - only show when document is displayed */}
            {generatedContent && !showForm && (
              <button 
                onClick={handleCopy}
                className="h-[42px] px-4 flex items-center gap-2 border border-gray-300 rounded-[10px] hover:bg-gray-50 transition-colors text-gray-700"
              >
                <Copy className="w-4 h-4" />
                <span className="text-base">Copy</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* SCROLLABLE CONTENT */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="container">
        {/* Ask AI Bar */}
        {showAskAI && (
          <div className="px-8 pt-6">
            <AskAIBar
              isOpen={showAskAI}
              onClose={() => setShowAskAI(false)}
              onSend={handleAskAI}
              placeholder={config.askAIPlaceholder}
            />
          </div>
        )}

        {/* Content Area */}
        {generatedContent && !showForm ? (
          <div className="px-8 py-8">
            {activeView === 'architecture' ? (
              <ArchitectureTemplate markdown={generatedContent} />
            ) : (
              <ApplicationAreasTemplate markdown={generatedContent} />
            )}
          </div>
        ) : (
          /* Empty State or Generate New Form */
          <div className="px-8 py-8">
            <div className="max-w-[585px] mx-auto">
              {/* Icon and Title */}
              <div className="text-center mb-8">
                {/* Icon */}
                <div className="w-20 h-20 mx-auto mb-6 bg-[#e0e7ff] rounded-[14px] flex items-center justify-center">
                  <Icon className="w-10 h-10 text-brand-primary" />
                </div>

                <h2 className="text-lg font-medium text-gray-900 mb-3">
                  {areaName ? `Define ${areaName}` : config.emptyTitle}
                </h2>

                <p className="text-base text-gray-600 max-w-[448px] mx-auto leading-6">
                  {areaName 
                    ? `Generate detailed specifications for ${areaName} by describing its features, components, and functionality.`
                    : config.emptyDescription}
                </p>
              </div>

              {/* Input Area with File Attachment */}
              <ArtifactGeneratorInput
                key={activeView}
                placeholder={areaName 
                  ? `Describe ${areaName}... (e.g., 'Key features, user interactions, data requirements')`
                  : config.placeholder}
                onGenerate={handleGenerate}
                isGenerating={isGenerating}
              />

              {/* Footer Text */}
              <p className="text-center text-base text-gray-500 mt-6">
                AI will analyze your input and generate comprehensive content
              </p>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

export type { DesignView };
